<template>
  <div
      :class="[
        'token-input',
        `type-${type}`,
        `size-${size}`,
        disabled && 'disabled',
        focused && 'focused',
      ]"
      @click="focus">
    <div
        v-for="(token, index) of value"
        :key="token"
        :class="[
          'token',
          selectedTokenIndex === index && 'selected',
        ]"
        @click="tokenClick($event, index)">
      <span>
        {{ token }}
      </span>
      <base-button
        v-if="showDeleteButtons"
        icon="close"
        size="tiny"
        type="transparent"
        @click="removeToken(token, $event)"/>
    </div>
    <input
        ref="input"
        v-model="inputValue"
        type="text"
        :placeholder="placeholder"
        @focus="onFocus"
        @blur="onBlur"
        @keypress.enter="addToken($event.target.value)"
        @keydown="keydown($event)"
        @keyup="keyup($event)">
  </div>
</template>

<script>
import BaseButton from '@/components/core/BaseButton.vue';

export default {
  name: 'BaseTokenInput',
  components: {
    BaseButton,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: () => false,
    },
    type: {
      type: String,
      default: 'normal',
      validator: value => [
        'normal',
        'transparent',
      ].includes(value),
    },
    size: {
      type: String,
      default: 'normal',
      validator: value => [
        'small',
        'normal',
      ].includes(value),
    },
    showDeleteButtons: {
      type: Boolean,
      default: true,
    },
    placeholder: String,
  },
  data() {
    return {
      inputValue: '',
      focused: false,
      selectedTokenIndex: -1,
      keyDown: false,
    };
  },
  methods: {
    addToken(token) {
      if (this.value.includes(token)) {
        return;
      }

      this.value.push(token);
      this.inputValue = '';
      this.$emit('input', this.value);
      this.$emit('tokenAdded', token);
    },
    removeToken(token, event) {
      if (event) {
        event.stopPropagation();
      }

      const index = this.value.indexOf(token);
      if (index === -1) {
        return;
      }

      this.value.splice(index, 1);
      this.$emit('input', this.value);
      this.$emit('tokenRemoved', token);

      this.focus();
    },
    tokenClick(event, index) {
      event.stopPropagation();
      this.$refs.input.focus();
      this.selectedTokenIndex = index;
    },
    focus() {
      this.$refs.input.focus();
      this.selectedTokenIndex = -1;
    },
    onFocus() {
      this.focused = true;
    },
    onBlur() {
      this.focused = false;
      this.selectedTokenIndex = -1;
    },
    keydown(event) {
      if (this.inputValue !== '') {
        this.selectedTokenIndex = -1;
        return;
      }
      switch (event.key) {
        case 'Backspace':
          if (this.selectedTokenIndex === -1) {
            this.selectedTokenIndex = this.value.length - 1;
          } else if (this.keyDown) {
            break;
          } else {
            this.removeToken(this.value[this.selectedTokenIndex]);
            this.selectedTokenIndex = -1;
          }
          break;
        case 'Delete':
          if (this.selectedTokenIndex !== -1) {
            this.removeToken(this.value[this.selectedTokenIndex]);
            this.selectedTokenIndex = -1;
          }
          break;
        case 'ArrowLeft':
          if (this.selectedTokenIndex === -1) {
            this.selectedTokenIndex = this.value.length - 1;
          } else if (this.selectedTokenIndex > 0) {
            this.selectedTokenIndex--;
          } else {
            this.selectedTokenIndex = -1;
          }
          break;
        case 'ArrowRight':
          if (this.selectedTokenIndex === -1) {
            this.selectedTokenIndex = 0;
          } else if (this.selectedTokenIndex < this.value.length - 1) {
            this.selectedTokenIndex++;
          } else {
            this.selectedTokenIndex = -1;
          }
          break;
        case 'ArrowUp':
          this.selectedTokenIndex = 0;
          break;
        case 'ArrowDown':
          this.selectedTokenIndex = this.value.length - 1;
          break;
        default:
          this.selectedTokenIndex = -1;
      }
      this.keyDown = true;
    },
    keyup() {
      this.keyDown = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';

.token-input {
  align-items: flex-start;
  color: $text;
  cursor: text;
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  outline: none;
  transition: background .2s;

  .token {
    align-items: center;
    cursor: pointer;
    display: flex;
    user-select: none;
    transition: background .2s;
  }

  input {
    background: transparent;
    border: none;
    color: $text;
    flex-grow: 1;
    outline: none;
  }

  &.disabled {
    color: $disabled-text;
  }

  &.type- {
    &normal {
      background: $input-background;

      &.focused {
        background: $input-background-focus;
      }

      &.disabled {
        background: $input-background-disabled;
      }

      .token {
        background: $dark-gray-700;

        &:hover {
          background: $dark-gray-400;
        }

        &.selected {
          background: $primary-600;

          &:hover {
            background: $primary-400;
          }
        }
      }
    }

    &transparent {
      background: transparent;

      .token {
        background: transparent;

        &:hover {
          background: $dark-gray-400;
        }

        &.selected {
          background: $primary-600;

          &:hover {
            background: $primary-400;
          }
        }
      }
    }
  }

  &.size- {
    &small {
      border-radius: 0.25rem;
      padding: 0.125rem 0.25rem;

      .token {
        border-radius: 0.25rem;
        font-size: 0.875rem;
        margin: 0.125rem 0.25rem 0.125rem 0;
        padding: 0.0625rem;

        span {
          line-height: 1rem;
          margin: 0.125rem 0.25rem;
        }

        button {
          margin: 0 !important;
        }
      }

      input {
        font-size: 0.875rem;
        line-height: 1.25rem;
        margin: 0.1875rem 0.3125rem;
      }
    }

    &normal {
      border-radius: 0.25rem;
      padding: 0.125rem 0.25rem;

      .token {
        border-radius: 0.25rem;
        font-size: 1rem;
        margin: 0.125rem 0.25rem 0.125rem 0;
        padding: 0.125rem;

        span {
          line-height: 1.25rem;
          margin: 0.125rem 0.25rem;
        }
      }

      input {
        font-size: 1rem;
        line-height: 1.25rem;
        margin: 0.375rem 0.25rem;
      }
    }
  }
}
</style>
